import _colors from "../../dict/colors.js";
const colors = _colors;

/**
 * Converts RGB hex to BGR hex for ASS format
 * @param {string} rgbColor - Hex color in RGB format (e.g., "FF0000" or "#FF0000")
 * @returns {string} - Hex color in BGR format (e.g., "0000FF")
 */
function convertRGBtoBGR(rgbColor) {
  const hex = rgbColor.replace("#", "");
  return hex.substring(4) + hex.substring(2, 4) + hex.substring(0, 2);
}
function getHexColor(color) {
  if (!color) {
    return null;
  }
  const normalizedColor = color.trim().toLowerCase().replace(/\s*!important\s*$/i, "");
  if (colors.hexColorMapping && colors.hexColorMapping[normalizedColor]) {
    return colors.hexColorMapping[normalizedColor];
  }
  if (/^#[0-9a-f]{6}$/i.test(normalizedColor)) {
    return normalizedColor;
  }
  return null;
}
function parseStyleDeclarations(style) {
  return style.split(";").reduce((declarations, declaration) => {
    const separatorIndex = declaration.indexOf(":");
    if (separatorIndex === -1) {
      return declarations;
    }
    const property = declaration.substring(0, separatorIndex).trim().toLowerCase();
    const value = declaration.substring(separatorIndex + 1).trim();
    if (property) {
      declarations[property] = value;
    }
    return declarations;
  }, {});
}

/**
 * Replaces HTML color styles with ASS inline color codes.
 * Handles color and background-color on span, strong, or em tags.
 * 
 * Example inputs:
 *   <span style="color: green">Hello!</span>
 *   <span style="background-color: green">Hello!</span>
 *   <strong style="color: green;"><em>Hello!</em></strong>
 * 
 * Example output:
 *   {\c&H0000FF00&}Hello!{\c}
 *   {\3c&H00FF00&}{\3a&H00&}Hello!{\3a}{\3c}
 *   {\c&H0000FF00&}<em>Hello!</em>{\c}
 * 
 * @param {string} text - HTML text with color span tags
 * @returns {string} - Text with ASS color codes
 */
export default (function replaceColorTagsAss(text) {
  if (!text) return text;

  // Match style attributes on span, strong, or em tags.
  const colorStyleRegex = /<(span|strong|em)\b[^>]*\bstyle\s*=\s*(?:"([^"]*)"|'([^']*)'|([^>\s]+))[^>]*>([\s\S]*?)<\/\1>/gi;
  let result = text;
  let hasMatch = true;

  // Process iteratively to handle nested tags
  while (hasMatch) {
    hasMatch = false;
    result = result.replace(colorStyleRegex, (match, tag, doubleQuotedStyle, singleQuotedStyle, unquotedStyle, content) => {
      hasMatch = true;
      const style = doubleQuotedStyle || singleQuotedStyle || unquotedStyle || "";
      const declarations = parseStyleDeclarations(style);
      const foregroundHexColor = getHexColor(declarations.color);
      const backgroundHexColor = getHexColor(declarations["background-color"]);
      if (!foregroundHexColor && !backgroundHexColor) {
        // Unknown colors return content without color formatting but preserve inner content.
        return content;
      }
      let openingTags = "";
      let closingTags = "";
      if (foregroundHexColor) {
        const bgrColor = convertRGBtoBGR(foregroundHexColor).toUpperCase();
        openingTags += `{\\c&H00${bgrColor}&}`;
        closingTags = `{\\c}` + closingTags;
      }
      if (backgroundHexColor) {
        const bgrColor = convertRGBtoBGR(backgroundHexColor).toUpperCase();
        openingTags += `{\\3c&H${bgrColor}&}{\\3a&H00&}`;
        closingTags = `{\\3a}{\\3c}` + closingTags;
      }
      return `${openingTags}${content}${closingTags}`;
    });
  }
  return result;
});